import { FormEvent, useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RichTextEditor } from '../../components/editor/RichTextEditor';
import { ProductCapabilitiesPanel } from '../../components/salesProducts/ProductCapabilitiesPanel';
import { ProductImageField } from '../../components/salesProducts/ProductImageField';
import { ProductMarketSegmentsPanel } from '../../components/salesProducts/ProductMarketSegmentsPanel';
import { ProductResourcesPanel } from '../../components/salesProducts/ProductResourcesPanel';
import { ProductBuyerPersonasPanel } from '../../components/salesProducts/ProductBuyerPersonasPanel';
import { ProductPricesPanel } from '../../components/salesProducts/ProductPricesPanel';
import { ProductLinkedApplicationsPanel } from '../../components/salesProducts/ProductLinkedApplicationsPanel';
import { canWriteProductCatalog } from '../../components/salesProducts/productCatalogAccess';
import { ProductWorkspacePanel, ProductWorkspaceTabId } from '../../components/salesProducts/ProductWorkspacePanel';
import { SettingsTabBar } from '../../components/settings/SettingsTabBar';
import { SettingsTabItem } from '../../components/settings/SettingsTabItem';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { RoundedCheckbox } from '../../components/ui/RoundedCheckbox';
import { PageCard } from '../../components/layout/page/PageCard';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  SalesProductUpsertPayload,
  createSalesProduct,
  getSalesProduct,
  listSalesProducts,
  updateSalesProduct,
} from '../../services/salesProductsApi';
import { nextSortOrder } from '../../utils/sortOrderList';
import { ProductAreaScope } from './productAreaScope';

type ProductEditTabId =
  | 'identity'
  | 'content'
  | 'capabilities'
  | 'segments'
  | 'resources'
  | 'personas'
  | 'prices'
  | 'applications'
  | ProductWorkspaceTabId;

const emptyForm: SalesProductUpsertPayload = {
  name: '',
  logoBase64: '',
  coverImageBase64: '',
  elevatorPitchHtml: '',
  valuePropositionHtml: '',
  descriptionHtml: '',
  executiveSummaryHtml: '',
  modulesAndFeaturesHtml: '',
  featuresHtml: '',
  icpHtml: '',
  segmentsHtml: '',
  isActive: true,
  sortOrder: 0,
};

const BASE_TABS: SettingsTabItem<ProductEditTabId>[] = [
  { id: 'identity', label: 'Identidade' },
  { id: 'content', label: 'Conteúdo' },
];

const CATALOG_TABS: SettingsTabItem<ProductEditTabId>[] = [
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'segments', label: 'Segmentos' },
  { id: 'resources', label: 'Recursos' },
  { id: 'personas', label: 'Personas' },
  { id: 'prices', label: 'Precificação' },
];

const WORKSPACE_TABS: SettingsTabItem<ProductEditTabId>[] = [
  { id: 'requirements', label: 'Requisitos' },
  { id: 'roadmap', label: 'Roadmap' },
  { id: 'backlog', label: 'Backlog' },
  { id: 'risks', label: 'Riscos' },
  { id: 'issues', label: 'Problemas' },
  { id: 'changes', label: 'Mudanças' },
];

function isWorkspaceTab(tab: ProductEditTabId): tab is ProductWorkspaceTabId {
  return (
    tab === 'requirements'
    || tab === 'backlog'
    || tab === 'roadmap'
    || tab === 'risks'
    || tab === 'issues'
    || tab === 'changes'
  );
}

export function ProductEditPage({ scope = 'commercial' }: { scope?: ProductAreaScope }) {
  const isProductArea = scope === 'product';
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const { canWriteModule } = usePermissions();
  const canWriteCatalog = canWriteProductCatalog(canWriteModule);
  const canWritePrices = canWriteModule('sales.products');
  const canWriteWorkspace = canWriteModule('products.workspace');
  const canWrite = isProductArea ? canWriteCatalog : canWritePrices;
  const listPath = isProductArea ? '/products' : '/sales/products';
  const initialTab: ProductEditTabId = 'identity';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<SalesProductUpsertPayload>(emptyForm);
  const [activeTab, setActiveTab] = useState<ProductEditTabId>(initialTab);
  const [loadedTabs, setLoadedTabs] = useState<Set<ProductEditTabId>>(
    () => new Set<ProductEditTabId>([initialTab]),
  );

  const tabs = useMemo(() => {
    if (isNew) return BASE_TABS;
    if (isProductArea) {
      return [
        ...BASE_TABS,
        ...CATALOG_TABS,
        ...WORKSPACE_TABS,
        { id: 'applications' as const, label: 'Aplicações' },
      ];
    }
    return [...BASE_TABS, ...CATALOG_TABS];
  }, [isNew, isProductArea]);

  useEffect(() => {
    if (!isNew) return;

    void (async () => {
      try {
        const result = await listSalesProducts({ activeOnly: false, page: 1, pageSize: 500 });
        setForm((current) => ({ ...current, sortOrder: nextSortOrder(result.items) }));
      } catch {
        // Mantém sortOrder padrão em caso de falha ao listar.
      }
    })();
  }, [isNew]);

  useEffect(() => {
    if (isNew) return;

    void (async () => {
      setLoading(true);
      try {
        const product = await getSalesProduct(id!);
        setForm({
          name: product.name,
          logoBase64: product.logoBase64,
          coverImageBase64: product.coverImageBase64,
          elevatorPitchHtml: product.elevatorPitchHtml,
          valuePropositionHtml: product.valuePropositionHtml,
          descriptionHtml: product.descriptionHtml,
          executiveSummaryHtml: product.executiveSummaryHtml,
          modulesAndFeaturesHtml: product.modulesAndFeaturesHtml,
          featuresHtml: product.featuresHtml,
          icpHtml: product.icpHtml,
          segmentsHtml: product.segmentsHtml,
          isActive: product.isActive,
          sortOrder: product.sortOrder,
        });
      } catch {
        toast.error('Falha ao carregar produto.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  function handleTabChange(tabId: ProductEditTabId): void {
    setActiveTab(tabId);
    setLoadedTabs((current) => {
      if (current.has(tabId)) {
        return current;
      }

      const next = new Set(current);
      next.add(tabId);
      return next;
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    setSaving(true);
    try {
      const saved = isNew
        ? await createSalesProduct(form)
        : await updateSalesProduct(id!, form);
      toast.success(isNew ? 'Produto criado.' : 'Produto atualizado.');
      navigate(isProductArea ? `/products/${saved.id}` : `/sales/products/${saved.id}`);
    } catch {
      toast.error('Falha ao salvar produto.');
    } finally {
      setSaving(false);
    }
  }

  if (!isProductArea && !canWritePrices) {
    return <p className="text-secondary-600">Você não possui permissão para editar produtos.</p>;
  }

  if (isProductArea && isNew && !canWrite) {
    return <p className="text-secondary-600">Você não possui permissão para cadastrar produtos.</p>;
  }

  if (loading) {
    return <p className="text-secondary-500">Carregando...</p>;
  }

  const showProductForm = activeTab === 'identity' || activeTab === 'content';

  return (
    <PageShell variant="form">
      <div>
        <Link to={listPath} className="erp-link-back">
          ← Voltar para produtos
        </Link>
        <PageHeader
          title={isNew ? 'Novo produto' : form.name || 'Editar produto'}
        />
      </div>

      <SettingsTabBar tabs={tabs} activeTab={activeTab} onChange={handleTabChange} />

      {showProductForm ? (
        <form onSubmit={(event) => void handleSubmit(event)} className="erp-form">
          {activeTab === 'identity' ? (
            <PageCard title="Identidade" subtitle="Nome, status e imagens usadas na página do produto.">
              <div className="space-y-5">
                <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
                  <label className="erp-label">
                    Nome
                    <input
                      className="erp-input"
                      value={form.name}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, name: event.target.value }))
                      }
                      required
                      placeholder="Ex.: Know — Inteligência e Governança do Conhecimento"
                    />
                  </label>

                  <label className="flex h-[42px] items-center gap-2.5 rounded-lg border border-secondary-200 bg-secondary-50 px-3.5 text-sm text-secondary-800">
                    <RoundedCheckbox
                      checked={form.isActive}
                      onChange={(event) =>
                        setForm((current) => ({ ...current, isActive: event.target.checked }))
                      }
                    />
                    Produto ativo
                  </label>
                </div>

                <div className="grid gap-5 md:grid-cols-[11rem_minmax(0,1fr)]">
                  <ProductImageField
                    label="Logo"
                    hint="PNG ou SVG, fundo claro"
                    value={form.logoBase64 ?? ''}
                    variant="logo"
                    onChange={(dataUrl) =>
                      setForm((current) => ({ ...current, logoBase64: dataUrl }))
                    }
                    onClear={() => setForm((current) => ({ ...current, logoBase64: '' }))}
                  />
                  <ProductImageField
                    label="Imagem de capa"
                    hint="Recomendado 1600×900"
                    value={form.coverImageBase64 ?? ''}
                    variant="cover"
                    onChange={(dataUrl) =>
                      setForm((current) => ({ ...current, coverImageBase64: dataUrl }))
                    }
                    onClear={() => setForm((current) => ({ ...current, coverImageBase64: '' }))}
                  />
                </div>
              </div>
            </PageCard>
          ) : null}

          {loadedTabs.has('content') ? (
            <div className={activeTab === 'content' ? 'space-y-6' : 'hidden'}>
              <PageCard title="Elevator pitch">
                <RichTextEditor
                  value={form.elevatorPitchHtml ?? ''}
                  onChange={(value) => setForm((current) => ({ ...current, elevatorPitchHtml: value }))}
                  placeholder="Frase de impacto do produto"
                />
              </PageCard>

              <PageCard title="Proposta de valor">
                <RichTextEditor
                  value={form.valuePropositionHtml ?? ''}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, valuePropositionHtml: value }))
                  }
                />
              </PageCard>

              <PageCard title="Resumo executivo">
                <RichTextEditor
                  value={form.executiveSummaryHtml ?? ''}
                  onChange={(value) =>
                    setForm((current) => ({ ...current, executiveSummaryHtml: value }))
                  }
                />
              </PageCard>

              <PageCard title="Descrição completa">
                <RichTextEditor
                  value={form.descriptionHtml ?? ''}
                  onChange={(value) => setForm((current) => ({ ...current, descriptionHtml: value }))}
                />
              </PageCard>

              <p className="rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-sm text-secondary-600">
                Módulos, recursos, segmentos e personas são gerenciados nas abas dedicadas do produto,
                a partir dos respectivos catálogos.
              </p>
            </div>
          ) : null}

          <FormActionBar
            showCancel={!isNew}
            onCancel={() => navigate(listPath)}
            cancelLabel="Cancelar"
          >
            <button type="submit" disabled={saving || !canWrite} className="erp-btn-primary">
              {saving ? 'Salvando...' : 'Salvar produto'}
            </button>
          </FormActionBar>
        </form>
      ) : null}

      {!isNew && id && loadedTabs.has('capabilities') ? (
        <div className={activeTab === 'capabilities' ? undefined : 'hidden'}>
          <ProductCapabilitiesPanel productId={id} />
        </div>
      ) : null}

      {!isNew && id && loadedTabs.has('segments') ? (
        <div className={activeTab === 'segments' ? undefined : 'hidden'}>
          <ProductMarketSegmentsPanel productId={id} />
        </div>
      ) : null}

      {!isNew && id && loadedTabs.has('resources') ? (
        <div className={activeTab === 'resources' ? undefined : 'hidden'}>
          <ProductResourcesPanel productId={id} />
        </div>
      ) : null}

      {!isNew && id && loadedTabs.has('personas') ? (
        <div className={activeTab === 'personas' ? undefined : 'hidden'}>
          <ProductBuyerPersonasPanel productId={id} />
        </div>
      ) : null}

      {!isNew && id && loadedTabs.has('prices') ? (
        <div className={activeTab === 'prices' ? undefined : 'hidden'}>
          <ProductPricesPanel productId={id} readOnly={isProductArea && !canWritePrices} />
        </div>
      ) : null}

      {!isNew && id && isWorkspaceTab(activeTab) && loadedTabs.has(activeTab) ? (
        <ProductWorkspacePanel productId={id} canWrite={canWriteWorkspace} activeTab={activeTab} />
      ) : null}

      {isProductArea && !isNew && id && loadedTabs.has('applications') ? (
        <div className={activeTab === 'applications' ? undefined : 'hidden'}>
          <ProductLinkedApplicationsPanel productId={id} />
        </div>
      ) : null}
    </PageShell>
  );
}
